import { COLORS } from '@/config/colors';
import { css, fillOutlined, circle, roundedPoly, shade } from './draw-utils';
import type { ProceduralDef } from '../types';

/**
 * Player ship hull. Nose points in the +X direction (right); code rotates it.
 * Wing pods are a SEPARATE asset ('player/ship/pods') — they show supershot charge.
 */
export const playerShipBase: ProceduralDef = {
  key: 'player/ship/base',
  width: 128,
  height: 128,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const cx = w / 2;
    const cy = h / 2;
    const OW = 6;

    // Wings — swept back, rounded tips
    roundedPoly(
      ctx,
      [
        [cx + 12, cy - 6],
        [cx - 28, cy - 44],
        [cx - 44, cy - 34],
        [cx - 24, cy - 2],
      ],
      8,
    );
    fillOutlined(ctx, COLORS.playerGreen, OW);
    roundedPoly(
      ctx,
      [
        [cx + 12, cy + 6],
        [cx - 28, cy + 44],
        [cx - 44, cy + 34],
        [cx - 24, cy + 2],
      ],
      8,
    );
    fillOutlined(ctx, COLORS.playerGreen, OW);

    // Wing shadow tone (lower half)
    roundedPoly(
      ctx,
      [
        [cx + 4, cy + 8],
        [cx - 26, cy + 40],
        [cx - 38, cy + 32],
        [cx - 22, cy + 6],
      ],
      6,
    );
    ctx.fillStyle = css(shade(COLORS.playerGreen, 0.8));
    ctx.fill();

    // Hull — long arrow; distinctly pointed nose
    roundedPoly(
      ctx,
      [
        [cx + 52, cy],
        [cx + 8, cy - 22],
        [cx - 30, cy - 13],
        [cx - 36, cy],
        [cx - 30, cy + 13],
        [cx + 8, cy + 22],
      ],
      9,
    );
    fillOutlined(ctx, COLORS.playerCream, OW);

    // Spine stripe (green) — tapers toward the nose
    roundedPoly(
      ctx,
      [
        [cx + 40, cy],
        [cx + 6, cy - 12],
        [cx - 26, cy - 7],
        [cx - 26, cy + 7],
        [cx + 6, cy + 12],
      ],
      7,
    );
    ctx.fillStyle = css(COLORS.playerGreen);
    ctx.fill();

    // Cockpit — amber dome, slightly forward
    circle(ctx, cx + 14, cy, 12);
    fillOutlined(ctx, COLORS.playerYellow, OW - 1);
    circle(ctx, cx + 17, cy - 4, 4.5);
    ctx.fillStyle = css(shade(COLORS.playerYellow, 1.45));
    ctx.fill();

    // Nose tip highlight + tail cyan light
    circle(ctx, cx + 46, cy, 3.5);
    ctx.fillStyle = css(shade(COLORS.playerCream, 1.15));
    ctx.fill();
    circle(ctx, cx - 31, cy, 4);
    ctx.fillStyle = css(COLORS.playerCyan);
    ctx.fill();
    void h;
  },
};

/** Ship-local positions of the wing pods (128 canvas, center 64,64) */
export const POD_OFFSETS: ReadonlyArray<readonly [number, number]> = [
  [-34, -40],
  [-34, 40],
];

/**
 * Wingtip pods — supershot charge indicator.
 * 5 frames: 0=empty (gray), 1-3=fill arc grows (cyan), 4=fully charged (bright cyan + halo).
 * Drawn on the same 128 canvas with the same anchor as the ship → aligns on top.
 */
export const playerShipPods: ProceduralDef = {
  key: 'player/ship/pods',
  width: 128,
  height: 128,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 5,
  draw(ctx, w, h, frame) {
    const cx = w / 2;
    const cy = h / 2;
    const R = 10;
    const full = frame === 4;

    for (const [ox, oy] of POD_OFFSETS) {
      const px = cx + ox;
      const py = cy + oy;

      // Outer halo when fully charged
      if (full) {
        circle(ctx, px, py, R + 5);
        ctx.fillStyle = 'rgba(69, 217, 197, 0.35)';
        ctx.fill();
      }

      // Pod body
      circle(ctx, px, py, R);
      fillOutlined(ctx, full ? COLORS.playerCyan : shade(COLORS.playerYellow, 0.55), 5);

      // Fill arc (clockwise, starts at the top)
      if (frame > 0 && !full) {
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.arc(px, py, R - 1.5, -Math.PI / 2, -Math.PI / 2 + (Math.PI * 2 * frame) / 4);
        ctx.closePath();
        ctx.fillStyle = css(COLORS.playerCyan);
        ctx.fill();
      }

      // Glint point
      circle(ctx, px - 3, py - 3, 2.5);
      ctx.fillStyle = full ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.45)';
      ctx.fill();
    }
    void h;
  },
};

/** Soft shadow under the ship — drawn at render time with offset and alpha. */
export const playerShadow: ProceduralDef = {
  key: 'player/shadow',
  width: 128,
  height: 96,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const grad = ctx.createRadialGradient(w / 2, h / 2, 4, w / 2, h / 2, w / 2.4);
    grad.addColorStop(0, 'rgba(10, 6, 20, 0.55)');
    grad.addColorStop(1, 'rgba(10, 6, 20, 0)');
    ctx.beginPath();
    ctx.ellipse(w / 2, h / 2, w / 2.4, h / 3.2, 0, 0, Math.PI * 2);
    ctx.fillStyle = grad;
    ctx.fill();
  },
};

/** Rear thrust flame — 4-frame flicker. Extends to the left (backward). */
export const playerThrust: ProceduralDef = {
  key: 'player/ship/thrust',
  width: 64,
  height: 64,
  anchorX: 0.85,
  anchorY: 0.5,
  frames: 4,
  draw(ctx, w, h, frame) {
    const cy = h / 2;
    const rootX = w * 0.85;
    const len = [30, 40, 34, 44][frame]!;
    const girth = [9, 11, 10, 12][frame]!;

    ctx.beginPath();
    ctx.moveTo(rootX, cy - girth);
    ctx.quadraticCurveTo(rootX - len * 0.6, cy - girth * 0.5, rootX - len, cy);
    ctx.quadraticCurveTo(rootX - len * 0.6, cy + girth * 0.5, rootX, cy + girth);
    ctx.closePath();
    ctx.fillStyle = css(COLORS.dangerOrange);
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(rootX, cy - girth * 0.55);
    ctx.quadraticCurveTo(rootX - len * 0.35, cy - girth * 0.3, rootX - len * 0.55, cy);
    ctx.quadraticCurveTo(rootX - len * 0.35, cy + girth * 0.3, rootX, cy + girth * 0.55);
    ctx.closePath();
    ctx.fillStyle = css(COLORS.playerYellow);
    ctx.fill();
  },
};
